import { useCallback, useState } from "react";
import appLogger from "../../../infra/logger/appLogger";
import { useAppDispatch, useAppSelector } from "../../hook";
import { useTranslation } from "../../localization/hooks";
import { RateLimitFlows, useRateLimitErrors } from "../../miscellaneous";
import { UserFlow } from "../types";
import { createSubscriptionOrder } from "../userSlice";
import useUserInfo from "../useUserInfo";
const subscriptionLogger = appLogger.withTag("Subscription");
export function useSubscription() {
    const { t } = useTranslation();
    const { onlyUserId } = useUserInfo();
    const { getMessage: getRateLimitErrorMessage } = useRateLimitErrors();
    const dispatch = useAppDispatch();
    const { loadingType, isLoading } = useAppSelector((state) => state.userInfo);
    const [subscriptionError, setSubscriptionError] = useState(null);
    const onUserSubscribe = useCallback(async (orderData) => {
        try {
            setSubscriptionError(null);
            const result = await dispatch(createSubscriptionOrder(orderData)).unwrap();
            if (result?.error_message) {
                const errorMessage = result.error_message;
                setSubscriptionError({
                    errorType: UserFlow.SUBSCRIPTION,
                    errorMessage,
                });
            }
            return result;
        }
        catch (error) {
            const apiError = error;
            const rateLimitErrorMessage = getRateLimitErrorMessage({
                errorCode: apiError?.errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: RateLimitFlows.SUBSCRIBE,
                },
            });
            const errorMessage = rateLimitErrorMessage || apiError.message || t("error:fallbackError");
            setSubscriptionError({
                errorType: UserFlow.SUBSCRIPTION,
                errorCode: apiError?.errorCode,
                errorMessage: errorMessage,
            });
            subscriptionLogger.error(`Error while purchasing subscription ${errorMessage}`, {
                ...apiError,
                userId: onlyUserId,
            });
            throw new Error(errorMessage);
        }
    }, [dispatch, getRateLimitErrorMessage, t]);
    return {
        onUserSubscribe,
        isLoading,
        loadingType,
        errorType: subscriptionError?.errorType || null,
        errorMessage: subscriptionError?.errorMessage || null,
        errorCode: subscriptionError?.errorCode || null,
    };
}
